import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, FolderTree, Users, ShoppingCart, Truck, RotateCcw,
  Boxes, IndianRupee, BarChart3, ScrollText, Settings, Plug, LogOut, Bell, Menu, X, Zap,
  CreditCard, LifeBuoy, Wallet, Globe, LayoutTemplate, KeyRound, Link2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DemoBadge } from "@/components/common";
import { ChangePasswordDialog } from "@/components/ChangePassword";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/categories", label: "Categories", icon: FolderTree },
  { to: "/admin/agents", label: "Agents", icon: Users },
  { to: "/admin/plans", label: "Plans", icon: CreditCard },
  { to: "/admin/marketplace", label: "Marketplace", icon: Globe },
  { to: "/admin/domains", label: "Domains", icon: Link2 },
  { to: "/admin/templates", label: "Templates", icon: LayoutTemplate },
  { to: "/admin/inventory", label: "Inventory", icon: Boxes },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { to: "/admin/shipping", label: "Shipping", icon: Truck },
  { to: "/admin/returns", label: "Returns", icon: RotateCcw },
  { to: "/admin/accounting", label: "Accounting", icon: IndianRupee },
  { to: "/admin/withdrawals", label: "Withdrawals", icon: Wallet },
  { to: "/admin/reports", label: "Reports", icon: BarChart3 },
  { to: "/admin/integrations", label: "Integrations", icon: Plug },
  { to: "/admin/audit", label: "Audit Logs", icon: ScrollText },
  { to: "/admin/support", label: "Support", icon: LifeBuoy },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [cpOpen, setCpOpen] = useState(false);
  const { data: health } = useQuery({
    queryKey: ["int-health-mini"],
    queryFn: async () => (await api.get("/admin/integrations/health")).data,
    staleTime: 60000,
  });
  const allLive = !!health && health.razorpay?.mode === "live" && health.indiafast?.mode === "live" &&
    (health.config?.amazon?.configured || false) && (health.config?.flipkart?.configured || false);
  const { data: notifs } = useQuery({
    queryKey: ["notifs"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 30000,
  });

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-slate-800 px-5 py-4">
        <img src="/logo.png" alt="DropPartner" className="h-9 w-9 rounded-lg bg-white object-contain p-0.5 shadow-sm" />
        <div>
          <p className="text-sm font-bold tracking-tight text-slate-50">DropPartner</p>
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Control Center</p>
        </div>
      </div>
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4" data-testid="admin-nav">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
            data-testid={`admin-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                isActive ? "bg-orange-500/10 font-medium text-orange-400" : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-200"
              }`
            }>
            <n.icon size={16} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-800 p-4">
        <p className="truncate text-xs font-medium text-slate-300">{user?.name}</p>
        <p className="truncate text-[10px] uppercase tracking-wider text-slate-500">{user?.role?.replace("_", " ")}</p>
        <button data-testid="admin-logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
          className="mt-3 flex items-center gap-2 text-xs text-slate-500 transition-colors hover:text-rose-400">
          <LogOut size={13} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#F2F6FB] text-[#0F2440]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 border-r border-slate-200 bg-white lg:block">{sidebar}</aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 border-r border-slate-200 bg-white">{sidebar}</aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col lg:ml-60">
        <div className="h-1.5 w-full" style={{ background: "linear-gradient(90deg, #FF9933 0%, #FF9933 33.3%, #FFFFFF 33.3%, #FFFFFF 66.6%, #138808 66.6%, #138808 100%)" }} />
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white/95 px-4 py-3 shadow-[0_2px_16px_rgba(15,42,92,0.06)] backdrop-blur-xl sm:px-6">
          <div className="flex items-center gap-3">
            <button data-testid="admin-menu-btn" className="text-slate-400 lg:hidden" onClick={() => setOpen(true)}>
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
            {health ? (
              allLive ? (
                <span data-testid="live-mode-badge" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />LIVE
                </span>
              ) : (
                <DemoBadge label="DEMO MODE · SOME INTEGRATIONS MOCK" />
              )
            ) : null}
          </div>
          <div className="relative flex items-center gap-3">
            <button data-testid="admin-change-password-btn" onClick={() => setCpOpen(true)} title="Change password"
              className="text-slate-400 transition-colors hover:text-[#E85D04]">
              <KeyRound size={17} />
            </button>
            <span className="relative">
            <Bell size={18} className="text-slate-400" />
            {notifs?.unread > 0 && (
              <span data-testid="notif-count" className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-orange-500 px-1 text-[9px] font-bold text-white">
                {notifs.unread}
              </span>
            )}
            </span>
          </div>
        </header>
        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
      <ChangePasswordDialog open={cpOpen} onClose={() => setCpOpen(false)} />
    </div>
  );
}
